import React, { useCallback, useState, memo } from 'react';
import { CardMedia, Box, Typography, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'; // Wishlist outlined
import FavoriteIcon from '@mui/icons-material/Favorite'; // Wishlist filled
import AddIcon from '@mui/icons-material/Add';
import { useWishlist } from '../../context/WishlistContext'; // Import wishlist context hook
import { useTranslation } from 'react-i18next';
import { getImageSrc, parseMultiLangContent } from '../../utils/imageHelper';
import { getActiveLanguage } from '../../i18n';
import { addToCart, fetchProductCartActivity } from '../../services/cartService'; // Import cart service
import QuickAddModal from './QuickAddModal'; // Import quick add modal
import ProductListColorOptions from './ProductListColorOptions';
import activityTracker from '../../utils/activityTracker'; // Import activity tracker
import { trackSelectItem } from '../../utils/marketingAnalytics';
import { COLORS, FONTS, HOVER_SCALE_IMG_SX } from '../../theme/tokens';

// Styled components
const StyledCardLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '& .product-card-image': {
    transition: `opacity 0.4s ease-in-out, ${HOVER_SCALE_IMG_SX['& img'].transition}`,
  },
  '&:hover .product-card-image': {
    ...HOVER_SCALE_IMG_SX['&:hover img'],
  },
  '&:hover .primary-product-image': {
    opacity: 0,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .product-card-image': {
      transition: 'none',
    },
    '&:hover .product-card-image': {
      transform: 'none',
    },
    '&:hover .primary-product-image': {
      opacity: 1,
    },
  },
}));

const StyledProductCardRoot = styled(Box)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

const ImageContainer = styled(Box)(() => ({
  position: 'relative',
  paddingTop: '125%',
  backgroundColor: '#f6f4f0',
  borderRadius: '2px',
  overflow: 'hidden',
}));

const ImageActionOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  aspectRatio: '4 / 5',
  zIndex: 6,
  pointerEvents: 'none',
}));

const PrimaryCardMedia = styled(CardMedia)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: 1,
  zIndex: 2,
}));

const SecondaryCardMedia = styled(CardMedia)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 1,
}));

const SingleCardMedia = styled(CardMedia)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

const SaleTag = styled(Box)(() => ({
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 3,
  backgroundColor: COLORS.gold,
  color: COLORS.textPrimary,
  borderRadius: '2px',
  padding: '3px 9px',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
}));

const StyledCardContent = styled(Box)(() => ({
  flexGrow: 1,
  padding: '12px 2px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}));

const SwatchRow = styled(Box)(() => ({
  minHeight: '14px',
  '& > .MuiBox-root': {
    gap: '5px',
    marginTop: 0,
  },
  '& > .MuiBox-root > .MuiBox-root': {
    width: '12px',
    minWidth: '12px',
    height: '12px',
    border: '1px solid rgba(0,0,0,0.15)',
    boxShadow: 'none',
  },
}));

const ProductTitle = styled(Typography)(() => ({
  fontFamily: FONTS.heading,
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const PriceContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
}));

const OriginalPrice = styled(Typography)(() => ({
  textDecoration: 'line-through',
  color: '#9e9e9e',
  fontSize: '0.8125rem',
}));

const CurrentPrice = styled(Typography)(() => ({
  color: '#212121',
  fontSize: '0.9375rem',
  fontWeight: 700,
}));

// Helper functions
const formatPrice = (price) => {
  return typeof price === 'number' ? price.toFixed(2) : '0.00';
};

// Custom comparison function for React.memo
// Compares only the product properties that affect rendering
const arePropsEqual = (prevProps, nextProps) => {
  const prev = prevProps.product;
  const next = nextProps.product;

  // If either is null/undefined, do a simple comparison
  if (!prev || !next) {
    return prev === next;
  }

  // Compare only the properties that affect rendering
  return (
    prev.product_id === next.product_id &&
    prev.product_title === next.product_title &&
    prev.product_price === next.product_price &&
    prev.product_price_discounted === next.product_price_discounted &&
    prev.product_image === next.product_image &&
    prev.second_image === next.second_image &&
    prev.product_slug === next.product_slug &&
    prev.product_quantity === next.product_quantity &&
    prev.active === next.active &&
    prev.variation_id_prod === next.variation_id_prod &&
    prev.enable_comment === next.enable_comment &&
    prev.enable_comment_custom === next.enable_comment_custom &&
    prev.final_price === next.final_price &&
    prev.product_name === next.product_name &&
    prev.colorOptions === next.colorOptions &&
    prevProps.imageLoading === nextProps.imageLoading &&
    prevProps.analyticsList?.listId === nextProps.analyticsList?.listId &&
    prevProps.analyticsList?.listName === nextProps.analyticsList?.listName &&
    prevProps.analyticsList?.index === nextProps.analyticsList?.index
  );
};

// The actual component
const StyledProductCard = memo(({ product, analyticsList, imageLoading = 'lazy' }) => {
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();
  void i18n.language; // re-render when storefront language changes
  const { addItem, removeItem, isInWishlist, loading: wishlistLoading } = useWishlist();
  const [modalOpen, setModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const title = parseMultiLangContent(product.product_title, activeLang);
  // Note: In the database, product_price contains the discounted price and
  // product_price_discounted contains the original price
  const hasDiscount = (
    parseFloat(product.product_price_discounted) > parseFloat(product.product_price)
  );
  const showSecondImage = product.second_image && product.product_image !== product.second_image;
  const productId = product.product_id; // Use consistent variable
  const isProductInWishlist = isInWishlist(productId);
  const accessibleProductName = title || 'product';
  const wishlistActionLabel = isProductInWishlist
    ? `Remove from wishlist — ${accessibleProductName}`
    : `Add to wishlist — ${accessibleProductName}`;
  const addToCartActionLabel = `Add to cart — ${accessibleProductName}`;

  // Check if product needs modal (has variations or comments enabled)
  const needsModal = (product.variation_id_prod && product.variation_id_prod > 0) ||
                     product.enable_comment ||
                     product.enable_comment_custom;

  const handleWishlistClick = useCallback((event) => {
    event.preventDefault(); // Prevent link navigation
    event.stopPropagation(); // Stop event bubbling

    if (wishlistLoading) return; // Prevent action while loading

    if (isProductInWishlist) {
      removeItem(productId);
    } else {
      addItem(productId);
    }
  }, [addItem, removeItem, productId, isProductInWishlist, wishlistLoading]); // Removed isInWishlist from dependencies

  const handleAddToCartClick = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (needsModal) {
      setModalOpen(true);
    } else {
      // Direct add to cart for simple products
      try {
        setAdding(true);
        await addToCart(productId, 1);
        // Dispatch cart updated event
        window.dispatchEvent(new CustomEvent('cartUpdated'));

        // Fetch cart activity to show social proof in success message
        let successMessage = t('product.addedToCart');
        try {
          const activityData = await fetchProductCartActivity(productId, 7);
          if (activityData?.data?.uniqueUsers >= 1) {
            const count = activityData.data.uniqueUsers;
            const userText = count === 1 ? 'customer has' : 'customers have';
            successMessage = `Item added to cart! ${count} other ${userText} this item recently.`;
          }
        } catch (e) {
          // If activity fetch fails, use default message
        }

        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
      } catch (error) {
        console.error('Error adding to cart:', error);
        setSnackbar({
          open: true,
          message: error.message || t('quickAdd.addFailedRetry'),
          severity: 'error'
        });
      } finally {
        setAdding(false);
      }
    }
  }, [productId, needsModal]);

  const handleQuickAdd = async (cartItem) => {
    try {
      setAdding(true);
      await addToCart(
        cartItem.product_id,
        cartItem.quantity,
        null, // giftCardData
        null, // customerDetails
        cartItem.selectedVariationName, // selectedVariationName
        cartItem.productComment, // productComment
        cartItem.productCommentCustom // productCommentCustom
      );
      // Dispatch cart updated event
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      setModalOpen(false);

      // Fetch cart activity to show social proof in success message
      let successMessage = t('product.addedToCart');
      try {
        const activityData = await fetchProductCartActivity(cartItem.product_id, 7);
        if (activityData?.data?.uniqueUsers >= 1) {
          const count = activityData.data.uniqueUsers;
          const userText = count === 1 ? 'customer has' : 'customers have';
          successMessage = `Item added to cart! ${count} other ${userText} this item recently.`;
        }
      } catch (e) {
        // If activity fetch fails, use default message
      }

      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      setSnackbar({
        open: true,
        message: error.message || t('quickAdd.addFailedRetry'),
        severity: 'error'
      });
    } finally {
      setAdding(false);
    }
  };

  // Check if product is inactive
  const isInactive = product.active === false || product.active === 0;

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <StyledProductCardRoot>
        <StyledCardLink
          to={`/product/${product.product_slug}`}
          onClick={() => {
            window.dispatchEvent(new CustomEvent('clearSearch')); // Dispatch event on click
            if (analyticsList) {
              trackSelectItem(product, analyticsList);
            }
            // Track product click
            activityTracker.trackProductClick({
              id: product.product_id,
              name: parseMultiLangContent(product.product_name),
              price: product.final_price || product.product_price
            });
          }}
        >
        <ImageContainer className="product-image-container">
          {hasDiscount && <SaleTag>{t('product.sale')}</SaleTag>}

          {/* Out of Stock Badge - Only show for active products with zero quantity */}
          {!isInactive && product.product_quantity <= 0 && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 4,
                  bgcolor: 'rgba(255,255,255,0.55)'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  zIndex: 5,
                  bgcolor: '#FFFFFF',
                  color: COLORS.textBody,
                  px: '9px',
                  py: '3px',
                  borderRadius: '2px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase'
                }}
              >
                {t('product.outOfStock')}
              </Box>
            </>
          )}

          {showSecondImage ? (
            <>
              <PrimaryCardMedia
                className="product-card-image primary-product-image"
                component="img"
                loading={imageLoading}
                image={getImageSrc(product.product_image, 'thumbs')} // Defaults to .webp
                alt={title}
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop
                  // Try original format if webp fails
                  e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${product.product_image}`; 
                }}
              />
              <SecondaryCardMedia
                className="product-card-image"
                component="img"
                loading="lazy"
                image={getImageSrc(product.second_image, 'thumbs')} // Defaults to .webp
                alt={`${title} - alternate view`}
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop
                  // Try original format if webp fails
                  e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${product.second_image}`; 
                }}
              />
            </>
          ) : (
            <SingleCardMedia
              className="product-card-image"
              component="img"
              loading={imageLoading}
              image={getImageSrc(product.product_image, 'thumbs')} // Defaults to .webp
              alt={title}
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                // Try original format if webp fails
                e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${product.product_image}`; 
              }}
            />
          )}
        </ImageContainer>
        <StyledCardContent>
          <SwatchRow>
             <ProductListColorOptions
               productId={productId}
               colors={product.colorOptions}
               disableLinks={true}
             />
          </SwatchRow>

          <ProductTitle component="div">
            {title}
          </ProductTitle>

          <PriceContainer>
            {hasDiscount ? (
              <>
                <CurrentPrice>
                  €{formatPrice(product.product_price)}
                </CurrentPrice>
                <OriginalPrice variant="body2">
                  €{formatPrice(product.product_price_discounted)}
                </OriginalPrice>
              </>
            ) : (
              <CurrentPrice>
                €{formatPrice(product.product_price)}
              </CurrentPrice>
            )}
          </PriceContainer>

          {/* Quantity Badge - Optional, consider if needed on card */}
          {/* <QuantityBadge quantity={product.product_quantity} /> */}
        </StyledCardContent>
        </StyledCardLink>

        <ImageActionOverlay>
          <Tooltip title={isProductInWishlist ? t('product.removeFromWishlist') : t('product.addToWishlist')}>
            <IconButton
              aria-label={wishlistActionLabel}
              onClick={handleWishlistClick}
              disabled={wishlistLoading}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                pointerEvents: 'auto',
                backgroundColor: 'rgba(255,255,255,0.85)',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                },
                color: COLORS.green,
                width: 36,
                height: 36,
                '& .MuiSvgIcon-root': {
                  fontSize: 18,
                },
              }}
            >
              {isProductInWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </Tooltip>

          {!isInactive && product.product_quantity > 0 && (
            <IconButton
              aria-label={addToCartActionLabel}
              onClick={handleAddToCartClick}
              disabled={adding}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                pointerEvents: 'auto',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                color: COLORS.green,
                '&:hover': {
                  backgroundColor: COLORS.green,
                  color: '#FFFFFF',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 19,
                },
              }}
            >
              <AddIcon />
            </IconButton>
          )}
        </ImageActionOverlay>
      </StyledProductCardRoot>

      {/* Quick Add Modal */}
      <QuickAddModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        product={product}
        onAddToCart={handleQuickAdd}
      />

      {/* Success/Error notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}, arePropsEqual);

// Set display name for debugging
StyledProductCard.displayName = 'StyledProductCard';

export default StyledProductCard;
